import { Component, TemplateFunction } from '@ribajs/core'
import { getElementFromEvent, getUID } from '@ribajs/utils'

interface Scope {
  toggle: SbDropdownComponent['toggle']
  show: SbDropdownComponent['show']
  hide: SbDropdownComponent['hide']
  isVisible: boolean
}

/**
 * A simple dropdown element, currently used only for the collection filter, but can be used in general and is not limited to the filter.
 * Can be expanded in the future, for example by using [Popper](https://popper.js.org/).
 * @see https://tailwindui.com/components/application-ui/elements/dropdowns
 */
export class SbDropdownComponent extends Component {
  public static tagName = 'sb-dropdown'

  public scope: Scope = {
    toggle: this.toggle,
    show: this.show,
    hide: this.hide,
    isVisible: false,
  }

  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element is registered as a custom element
   */
  constructor() {
    super()
  }

  public isVisible() {
    return this.scope.isVisible
  }

  public toggle() {
    if (this.scope.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }

  /** Open / show this dropdown element */
  public show() {
    this.scope.isVisible = true
    this.hideAll(this)
    this.onShown()
  }

  /** Hide / close this dropdown element */
  public hide() {
    this.scope.isVisible = false
    this.onHidden()
  }

  /**
   * Hide / close all dropdown elements
   * @param except Except this dropdown element
   */
  public hideAll(except?: SbDropdownComponent) {
    const dropdownEls = Array.from(
      document.getElementsByTagName(SbDropdownComponent.tagName),
    ) as SbDropdownComponent[]
    for (const dropdownEl of dropdownEls) {
      if (!except || except !== dropdownEl) {
        dropdownEl.hide()
      }
    }
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbDropdownComponent.observedAttributes)
  }

  /**
   * Called as soon as this custom element es removed from the dom
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Called before this custom element is bound and ready
   */
  async beforeBind() {
    this.initDropdown()
    this.addEventListeners()
    await super.beforeBind()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbDropdownComponent.tagName}-ready`)
  }

  protected initDropdown() {
    if (!this.id) {
      this.id = getUID(`sb-dropdown-`)
    }
  }

  protected _onShown() {
    // console.debug("onShown");
  }

  protected onShown = this._onShown.bind(this)

  protected _onHidden() {
    // console.debug("onHidden");
  }

  protected onHidden = this._onHidden.bind(this)

  /** Close this dropdown if the user clicks outside this dropdown */
  protected _onClickOutside(event: MouseEvent | TouchEvent) {
    const target = getElementFromEvent(event)
    const parentDropdown = target.closest<SbDropdownComponent>(
      SbDropdownComponent.tagName,
    )
    if (!parentDropdown || parentDropdown.id !== this.id) {
      this.hide()
    }
  }

  onClickOutside = this._onClickOutside.bind(this)

  protected addEventListeners() {
    window.addEventListener('click', this.onClickOutside)
  }

  protected removeEventListeners() {
    window.removeEventListener('click', this.onClickOutside)
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
